import { faTimesCircle as solidTimesCircle } from "@jewlr/pro-solid-svg-icons/faTimesCircle"
import PropTypes from "prop-types"
import React, { Component } from "react"
import { ErrorMessage } from "formik"
import styled from "styled-components"

import FontAwesomeIcon from "components/font-awesome-icon"

import Button from "./button"

const FieldWrapper = styled.div`
  margin-bottom: ${(props) => props.mb || "15px"};
  overflow: hidden;
  padding: ${(props) => props.p || "0 7px"};
`
const InputWrapper = styled.div`
  position: relative;
  display: flex;
  border: 1px solid
    ${(props) =>
      props.focused ? props.theme.colors.black : props.theme.colors.grey10};
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.grey4 : props.theme.colors.white};
`

const Label = styled.label`
  color: ${(props) =>
    props.disabled ? props.theme.colors.grey30 : props.theme.colors.grey65};
  display: inline-block;
  font-size: 10px;
  font-weight: normal;
  line-height: 1;
  opacity: ${(props) => (props.float ? 1 : 0)};
  padding: 0 10px;
  position: absolute;
  top: ${(props) => (props.float ? "7px" : "12px")};
  transition: all 200ms ease;
  visibility: ${(props) => (props.float ? "visible" : "hidden")};
`
const InputField = styled.input`
  -webkit-appearance: none;
  flex: 1;
  width: ${(props) => props.width || "100%"};
  max-width: 100%;
  padding: ${(props) => (props.float ? "16px 10px 0" : "10px")};
  height: 44px;

  font-size: ${(props) => (props.fontSize ? props.fontSize : "14px")};
  color: ${(props) => props.theme.colors.black};
  line-height: 1.2;
  border: none;
  border-radius: 0;
  outline: none;
  transition: all 200ms ease;
  background-color: transparent;

  ::-ms-clear {
    display: none;
  }

  :focus {
    outline: none;
    transition: none;
  }

  ::placeholder {
    color: ${(props) => props.theme.colors.grey35};
  }

  ::selection {
    background-color: hsl(209, 82%, 54%);
    color: white;
  }
  :-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 100px #fff inset;
  }
  ::-webkit-inner-spin-button {
    display: none;
  }
  ::-webkit-clear-button {
    display: none;
  }
  ::-webkit-calendar-picker-indicator {
    color: ${(props) => props.arrowColor || props.theme.colors.grey65};
    height: 12px;
    margin-bottom: 12px;
    margin-left: 12px;
    margin-right: -2px;
    opacity: 1;
    width: 11px;
  }
  :disabled {
    opacity: 1;
    color: ${(props) => props.theme.colors.grey30};
    -webkit-text-fill-color: ${(props) => props.theme.colors.grey30};
  }
`
const InputActions = styled.div`
  display: flex;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: all 0.2s ease;
  width: ${(props) => (props.show ? "auto" : 0)};
`
const CloseIcon = styled(Button).attrs({
  text: true,
  type: "button",
})`
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 18px;
  color: ${(props) => props.theme.colors.grey20};
  -webkit-tap-highlight-color: transparent;
`
const FieldError = styled(ErrorMessage)`
  margin-top: 5px;
  margin-bottom: 0;
  font-size: 10px;
  color: red;
`
const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  ${(props) => props.theme.mediaQueries.tablet`
    display: none;
  `}
`
/**
 * This FloatInput is a regular `input` element with the floating label applied to it
 * @param {string} [className] The className to pass to the inner `input` element
 * @param {string} [mb] margin-bottom for FieldWrapper
 * @param {string} [p] padding for FieldWrapper
 * @param {string} [id] The id to pass to the inner `input` element
 * @param {string} name The name to pass to the inner `input` element
 * @param {string} [type] The type to pass to the inner `input` element
 * @param {string} [label] The text to show in both the input placeholder/label
 * @param {string} [placeholder=label] The text to show in the placeholder.
 * @param {function} [onChange] The onChange to pass to the inner `input` element
 * @param {function} [onClick] The onClick to pass to the inner `input` element
 * @param {function} [onReturn] Callback on 'enter' keypress
 * @param {bool} [showClear=false] to show clear input 'x' icon
 * @param {array} [buttons] Button components to render inside FloatInput wrapper
 */
class FloatInput extends Component {
  constructor(props) {
    super(props)
    this.state = {
      float: this.props.value && this.props.value != "" ? true : false,
      focused: false,
      value: this.props.value || "",
    }
    this.hiddenInputRef = React.createRef()
    this.setInputRef = (ref) => {
      if (this.props.inputRef) {
        this.props.inputRef(ref)
      }
      this.inputRef = ref
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // In case the controlled component value gets updated outside of the context of this input,
    // we need to re-jig the float property
    if (
      (!prevProps.value || prevProps.value == "") &&
      this.props.value &&
      this.props.value != "" &&
      !this.state.float
    ) {
      this.setState({
        float: true,
      })
    }
  }

  handleChange(e) {
    this.setState({ value: e.target.value })

    if (e.target.value != "" && !this.state.float) {
      this.setState({ float: true })
    }

    if (this.props.onChange) {
      this.props.onChange(e)
    }
  }

  // Sets input value natively
  setNativeValue(element, value) {
    const valueSetter = Object.getOwnPropertyDescriptor(element, "value").set
    const prototype = Object.getPrototypeOf(element)
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(
      prototype,
      "value"
    ).set

    if (valueSetter && valueSetter !== prototypeValueSetter) {
      prototypeValueSetter.call(element, value)
    } else {
      valueSetter.call(element, value)
    }
  }

  handleClearInput(e) {
    e.preventDefault()
    this.setState({ value: "", float: false })

    // Trigger onChange event by setting the value to input natively
    const input = this.inputRef
    this.setNativeValue(input, "")
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.focus()
  }

  onKeyUp(e) {
    // Only set the state to floating if the input has changed from a blank string
    if (e.target.value && e.target.value != "" && !this.state.float) {
      this.setState({
        float: true,
      })
      // otherwise, kill the floating state if it is active
    } else if ((!e.target.value || e.target.value == "") && this.state.float) {
      this.setState({
        float: false,
      })
    }
    if (this.props.onKeyUp) {
      this.props.onKeyUp(e)
    }
  }

  onKeyDown(e) {
    let keyCode = e.keyCode || e.which || event.charCode
    if (this.props.onReturn && keyCode == 13) {
      e.preventDefault()
      this.props.onReturn(e)
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(e)
    }
  }

  onBlur(e) {
    if (this.state.focused) {
      this.setState({
        focused: false,
      })
    }
    if (this.props.onBlur) {
      this.props.onBlur(e)
    }
  }

  onFocus(e) {
    if (!this.state.focused) {
      this.setState({
        focused: true,
      })
    }
    if (this.props.onFocus) {
      this.props.onFocus(e)
    }
  }
  hiddenInputFocus(e) {
    this.props.onReturn(e)
    this.hiddenInputRef.current.blur()
  }

  componentDidCatch(error, info) {
    console.log("HAS ERROR: ", error)
    console.log("INFO: ", info)
  }

  render() {
    // Destructuring to avoid passing onReturn into the actual input
    // field, so React doesn't complain about the unknown prop
    const { onReturn, ...inputProps } = this.props
    return (
      <FieldWrapper
        mb={this.props.mb}
        p={this.props.p}
        className={this.props.gridClass}
      >
        <InputWrapper
          focused={this.state.focused}
          disabled={this.props.disabled}
        >
          <Label
            htmlFor={this.props.id || this.props.name}
            className={this.props.labelClass}
            disabled={this.props.disabled}
            float={this.state.float}
            focused={this.state.focused}
          >
            {this.props.label}
          </Label>
          <InputField
            arrowColor={this.arrowColor}
            {...inputProps}
            float={this.state.float}
            id={this.props.id || this.props.name}
            name={this.props.name}
            onBlur={(e) => this.onBlur(e)}
            onChange={(e) => this.handleChange(e)}
            onClick={this.props.onClick}
            onFocus={(e) => this.onFocus(e)}
            onKeyDown={(e) => this.onKeyDown(e)}
            onKeyUp={(e) => this.onKeyUp(e)}
            placeholder={this.props.placeholder || this.props.label}
            ref={this.setInputRef}
            type={this.props.type}
            value={this.props.value}
          />
          {/* Captures Android 'tab' focus */}
          {this.props.onReturn && (
            <HiddenInput
              onFocus={(e) => this.hiddenInputFocus(e)}
              ref={this.hiddenInputRef}
              type="text"
            />
          )}
          <InputActions show={this.state.float}>
            {this.props.showClear && (
              <CloseIcon
                onClick={(e) => this.handleClearInput(e)}
                tabIndex="-1"
              >
                <FontAwesomeIcon icon={solidTimesCircle} />
              </CloseIcon>
            )}
            {this.props.buttons && this.props.buttons.map((button) => button)}
          </InputActions>
        </InputWrapper>
        {this.props.name && <FieldError name={this.props.name} component="p" />}
      </FieldWrapper>
    )
  }
}

FloatInput.propTypes = {
  showClear: PropTypes.bool,
  value: PropTypes.string,
  buttons: PropTypes.array,
}

FloatInput.defaultProps = {
  showClear: false,
}

export default FloatInput
